// The v1 session: store + tick engine + sim + the Pixi world, wired the way
// src/main.ts wires the desktop build (PLAN-03 §4), minus everything the old
// overlay owned and the new shell replaces.
//
// One difference on purpose: the tick engine is driven by its own rAF loop, not
// by the Pixi frame loop. Pausing rendering when a screen covers the stage must
// not pause the game — the birds still live while you read the warning screen.

import { bus } from '../core/events';
import { GameStateStore } from '../core/store';
import type { GameState } from '../core/types';
import { t } from '../i18n';
import { evaluateBadges, recordBrooderTime, recordIncident, rolloverDay } from '../sim/progression';
import { getConfig } from '../sim/config';
import { runOfflineCatchup } from '../sim/offlineCatchup';
import { leagueForecast, stepLeagueWeather } from '../sim/forecast';
import { stepSimulation, type StepReport } from '../sim/simulate';
import { beginStormWatch, resolveStorm, shouldStormHit } from '../sim/storm';
import { simHoursPerTick } from '../sim/time';
import { brooderTsiFor, summarizeFacilities } from '../sim/welfare';
import { feederShortfall } from '../sim/access';
import { unlockFactForTrigger } from '../edu/facts';
import { WeatherService } from '../sim/weather';
import { TickEngine } from '../sim/tickEngine';
import { NotificationManager } from '../ui/notify';
import type { DevDashboard } from '../ui/devDashboard';
import { isRunOver } from '../sim/veteran';
import { CoopWorld } from './world';
import type { Shell } from './shell';

export interface SessionHooks {
  /** Toasts the shell owns. */
  onToast: (message: string) => void;
  /** The end of a Veteran run. The old overlay had a modal for it. */
  onRunOver: () => void;
  /** Tapping a bird in the diorama. */
  onBirdTap: (chickenId: string) => void;
  onTileTap?: (x: number, y: number) => void;
}

export class GameSession {
  readonly world: CoopWorld;
  private tickEngine: TickEngine | null = null;
  private weather: WeatherService | null = null;
  private notifications: NotificationManager | null = null;
  private devDashboard: DevDashboard | null = null;
  private lastFrameMs = 0;
  private frameHandle = 0;
  private running = false;

  constructor(
    readonly store: GameStateStore,
    private readonly shell: Shell,
    private readonly hooks: SessionHooks,
  ) {
    this.world = new CoopWorld(shell.stage, store, {
      onBirdTap: (id) => hooks.onBirdTap(id),
      onTileTap: hooks.onTileTap,
    });
    // An opaque screen stops the renderer. The clock below is separate, so the
    // birds keep living while the yard is not being drawn.
    shell.onStageChange((active) => this.world.setActive(active));
  }

  /** Replays the time the tab was away, then starts the clock and the weather. */
  start(): void {
    const state = this.store.getState();
    const catchup = runOfflineCatchup(state);

    const engine = new TickEngine(state.tickCount);
    this.tickEngine = engine;
    // Real-time only (REFOCUS-2026-09.md §4), like the desktop entry: the engine
    // always runs at 1x and the dev dashboard's speed buttons set it directly.
    engine.setSpeed(1);
    engine.onTick((tickCount) => this.step(tickCount));

    // Browsers throttle rAF in a hidden tab, so the resumed frame delta lies
    // about the gap. Visibility events are the truth; the engine turns them
    // into a capped, chunked catch-up.
    const onVisibility = (): void => {
      if (document.hidden) engine.markHidden(Date.now());
      else engine.markVisible(Date.now());
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', () => engine.markHidden(Date.now()));
    window.addEventListener('focus', () => engine.markVisible(Date.now()));

    // Browser notifications, for a tab that is in the background while the
    // chores are missed. Entirely optional: denied or unsupported, it degrades
    // to silence and the game plays exactly as before.
    const notifications = new NotificationManager(() => this.store.getState());
    this.notifications = notifications;
    notifications.start();

    const weather = new WeatherService();
    this.weather = weather;
    bus.on('WEATHER_CHANGED', ({ weather: next }) => {
      this.store.mutate((s) => {
        // A league save's weather is the season's, derived in the sim from the
        // league day. A live city forecast would put two players in the same
        // league day on different air, which is the thing REFOCUS §11 rules
        // out — and would make the sim depend on a network answer the server
        // replay cannot reproduce.
        if (leagueForecast(s)) return;
        // The player's storm prep state is ours, not the API's.
        s.weather = {
          ...next,
          stormAlert: s.storm.active ? s.weather.stormAlert : next.stormAlert,
        };
        if (
          !s.storm.active &&
          (next.stormAlert === 'warning' || next.stormAlert === 'watch')
        ) {
          beginStormWatch(s, false, 10);
        }
      });
    });
    if (leagueForecast(this.store.getState())) {
      // A league save never asks for a city: no coordinates leave the device
      // and nothing is fetched, because the season already knows the weather.
      this.store.mutate((s) => stepLeagueWeather(s));
    } else {
      // Weather follows a city the player picked, never the device's position:
      // the save carries the name and the service turns it back into a forecast.
      // Chained so the first poll already knows the city, rather than racing it.
      void weather.useSavedCity(this.store.getState().settings.weatherCity).then(() => weather.start());
    }

    this.clock();
    this.store.startAutosave();
    window.addEventListener('pagehide', () => void this.store.save());

    if (catchup.simulatedHours > 0.5) this.hooks.onToast(t('status.away', {
      hours: Math.round(catchup.simulatedHours),
      eggs: catchup.eggsLaid.length,
    }));
  }

  /** Boots the Pixi world. Deferred: onboarding never shows the diorama. */
  private mounting: Promise<void> | null = null;

  mountWorld(): Promise<void> {
    // Called on every coop render; the world mounts once.
    this.mounting ??= this.world.mount().then(() => {
      this.world.setState(this.store.getState());
    });
    return this.mounting;
  }

  /** A wholesale state swap (new game, import, dev override) needs a resync. */
  resync(): void {
    const state = this.store.getState();
    this.tickEngine?.setTickCount(state.tickCount);
    this.world.setState(state);
  }

  /**
   * The dev dashboard, which the old overlay used to own. Dev builds only: the
   * panels are Tailwind, so `import.meta.env.DEV` also loads the stylesheet
   * that makes them legible.
   */
  toggleDevDashboard(): void {
    if (!import.meta.env.DEV) return;
    if (this.devDashboard) {
      this.devDashboard.toggle();
      return;
    }
    void import('../ui/styles.css');
    void import('../ui/devDashboard').then(({ DevDashboard }) => {
      if (!this.tickEngine) return;
      this.devDashboard = new DevDashboard(this.shell.root, this.store, this.tickEngine);
      this.devDashboard.toggle();
    });
  }

  /**
   * A weather city was chosen in Settings. The label is the durable record —
   * the save keeps the name, the service keeps the coordinates — and the
   * forecast is refreshed at once so the pill is not stale behind the sheet.
   *
   * A league save has no city: its air is the season's, so there is nothing to
   * fetch and nothing for a name to change. The row is hidden there, and this
   * returns without asking the network.
   */
  async pickWeatherCity(label: string | null): Promise<void> {
    if (leagueForecast(this.store.getState())) return;
    this.store.mutate((s) => {
      if (label === null) delete s.settings.weatherCity;
      else s.settings.weatherCity = label;
    });
    await this.weather?.useSavedCity(label);
    await this.weather?.refresh();
  }

  stop(): void {
    this.running = false;
    this.shell.setStageActive(false);
    if (this.frameHandle) cancelAnimationFrame(this.frameHandle);
    this.weather?.stop();
    this.notifications?.stop();
    this.store.stopAutosave();
    this.world.destroy();
  }

  // --- the clock ------------------------------------------------------------

  private clock(): void {
    this.running = true;
    this.lastFrameMs = performance.now();
    const frame = (): void => {
      if (!this.running) return;
      const now = performance.now();
      const delta = Math.min(now - this.lastFrameMs, 250);
      this.lastFrameMs = now;
      this.tickEngine?.advance(delta);
      this.frameHandle = requestAnimationFrame(frame);
    };
    this.frameHandle = requestAnimationFrame(frame);
  }

  private step(tickCount: number): void {
    let report: StepReport | null = null;
    this.store.mutate((s) => {
      s.tickCount = tickCount;
      const hours = simHoursPerTick();
      report = stepSimulation(s, { hours });
      // The storm lands when its countdown runs out, drill or real.
      if (shouldStormHit(s)) resolveStorm(s);
      rolloverDay(s);
      this.trackBrooder(s, hours);
      evaluateBadges(s);
      // The one teachable moment that is about the yard rather than a bird: the
      // flock has outgrown the trough it came with. Recorded once, like every
      // other trigger, so the card is in the Encyclopedia whether or not the
      // player opens the feed screen and finds it.
      if (feederShortfall(s, getConfig(s.veteran.enabled ? 'veteran' : 'normal'))) {
        unlockFactForTrigger(s, 'event:feeder_crowded');
      }
    });
    // Published after the mutation, so every subscriber sees settled state.
    if (report) this.publish(report);
  }

  private trackBrooder(state: GameState, hours: number): void {
    const chick = state.flock.find((c) => c.stage === 'chick');
    if (!chick) return;
    const brooder = summarizeFacilities(state).brooders[0];
    if (!brooder) return;
    const config = getConfig(state.veteran.enabled ? 'veteran' : 'normal');
    recordBrooderTime(state, brooderTsiFor(chick, brooder, state.weather.tempF, config), hours);
  }

  /** Turns a simulation report into the events the shell listens for. */
  private publish(report: StepReport): void {
    for (const { chickenId, condition } of report.newConditions) {
      bus.emit('CHICKEN_SICK', { chickenId, condition });
    }
    for (const { chickenId, condition } of report.recoveries) {
      bus.emit('CHICKEN_RECOVERED', { chickenId, condition });
    }
    for (const chickenId of report.escapes) bus.emit('CHICKEN_ESCAPED', { chickenId });
    for (const chickenId of report.hatched) {
      const chick = this.store.getState().flock.find((c) => c.id === chickenId);
      if (chick) this.hooks.onToast(t('coop.hatched', { name: chick.name }));
    }
    for (const chickenId of report.deaths) {
      const bird = this.store.getState().flock.find((c) => c.id === chickenId);
      if (!bird) continue;
      // A named bird dying is the worst thing in the game, and it is never
      // silent: the cause is named (REFOCUS §11c).
      this.hooks.onToast(
        t('coop.diedToast', {
          name: bird.name,
          cause: bird.deathCause ? t(`death.${bird.deathCause}`) : t('runOver.none'),
        }),
      );
    }
    if (report.deaths.length > 0 && isRunOver(this.store.getState())) {
      this.hooks.onRunOver();
    }
    for (const { chickenId } of report.eggsLaid) {
      const bird = this.store.getState().flock.find((c) => c.id === chickenId);
      if (!bird) continue;
      bus.emit('EGG_LAID', {
        chickenId,
        tile: { x: Math.floor(bird.position.x), y: Math.floor(bird.position.y) },
      });
    }
  }
}

// The teachable moments the old shell toasted, kept because the lesson is the
// point (EDUCATION-DESIGN §1): the first occurrence unlocks its fact card.
export function wireTeachableMoments(store: GameStateStore, onToast: (message: string) => void): () => void {
  const offs: Array<() => void> = [];
  offs.push(
    bus.on('CHICKEN_SICK', ({ chickenId, condition }) => {
      const bird = store.getState().flock.find((c) => c.id === chickenId);
      store.mutate((s) => {
        unlockFactForTrigger(s, `condition:${condition}`);
        recordIncident(s);
      });
      onToast(
        t('toast.sick', {
          name: bird?.name ?? '',
          condition: t(`condition.${condition}`),
        }),
      );
    }),
  );
  offs.push(
    bus.on('CHICKEN_RECOVERED', ({ chickenId }) => {
      const bird = store.getState().flock.find((c) => c.id === chickenId);
      if (bird) onToast(t('toast.recovered', { name: bird.name }));
    }),
  );
  offs.push(
    bus.on('CHICKEN_ESCAPED', ({ chickenId }) => {
      const bird = store.getState().flock.find((c) => c.id === chickenId);
      store.mutate((s) => unlockFactForTrigger(s, 'event:first_escape'));
      if (bird) onToast(t('toast.escaped', { name: bird.name }));
    }),
  );
  offs.push(bus.on('STORM_WATCH', () => onToast(t('toast.stormWatch'))));
  return () => {
    for (const off of offs) off();
  };
}
